import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Save, Trash2, Plus, GripVertical, Settings, Eye, EyeOff,
  Type, Image, Layout, Square, Columns, List, Quote, Video, 
  ChevronRight, X, Copy, Undo, Redo, Download, Upload, Loader2,
  Globe, ExternalLink, Check
} from 'lucide-react';
import { adminFetch, isAdminAuthenticated } from '@/utils/adminAuth';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== BLOCK TYPES ====================
const BLOCK_TYPES = {
  hero: {
    name: 'Hero Section',
    icon: Layout,
    category: 'sections',
    defaultProps: {
      title: 'Welcome to Our Site',
      subtitle: 'Your amazing subtitle here',
      buttonText: 'Get Started',
      buttonLink: '#',
      backgroundImage: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1200',
      overlay: 'dark',
      height: 'large',
    },
  },
  text: {
    name: 'Text Block',
    icon: Type,
    category: 'content',
    defaultProps: {
      content: 'Enter your text here. You can add paragraphs, format text, and more.',
      align: 'left',
      size: 'medium',
    },
  },
  heading: {
    name: 'Heading',
    icon: Type,
    category: 'content',
    defaultProps: {
      text: 'Section Heading',
      level: 'h2',
      align: 'center',
    },
  },
  image: {
    name: 'Image',
    icon: Image,
    category: 'media',
    defaultProps: {
      src: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800',
      alt: 'Image description',
      caption: '',
      size: 'full',
    },
  },
  button: {
    name: 'Button',
    icon: Square,
    category: 'content',
    defaultProps: {
      text: 'Click Me',
      link: '#',
      style: 'primary',
      size: 'medium',
      align: 'center',
    },
  },
  columns: {
    name: 'Columns',
    icon: Columns,
    category: 'layout',
    defaultProps: {
      columns: 2,
      gap: 'medium',
      content: ['Column 1 content', 'Column 2 content'],
    },
  },
  list: {
    name: 'List',
    icon: List,
    category: 'content',
    defaultProps: {
      items: ['Item 1', 'Item 2', 'Item 3'],
      style: 'bullet',
    },
  },
  quote: {
    name: 'Quote',
    icon: Quote,
    category: 'content',
    defaultProps: {
      text: 'This is an inspirational quote that captures attention.',
      author: 'Author Name',
      style: 'bordered',
    },
  },
  spacer: {
    name: 'Spacer',
    icon: Square,
    category: 'layout',
    defaultProps: {
      height: 'medium',
    },
  },
  divider: {
    name: 'Divider',
    icon: Square,
    category: 'layout',
    defaultProps: {
      style: 'line',
      color: 'gray',
    },
  },
  video: {
    name: 'Video Embed',
    icon: Video,
    category: 'media',
    defaultProps: {
      url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      aspectRatio: '16:9',
    },
  },
  cta: {
    name: 'Call to Action',
    icon: Layout,
    category: 'sections',
    defaultProps: {
      title: 'Ready to Get Started?',
      description: 'Join us today and discover amazing possibilities.',
      buttonText: 'Sign Up Now',
      buttonLink: '#',
      style: 'gradient',
    },
  },
};

const CATEGORIES = {
  sections: { name: 'Sections', icon: Layout },
  content: { name: 'Content', icon: Type },
  media: { name: 'Media', icon: Image },
  layout: { name: 'Layout', icon: Columns },
};

// ==================== BLOCK PREVIEW COMPONENTS ====================
const BlockPreview = ({ block, isSelected, onClick }) => {
  const type = BLOCK_TYPES[block.type];
  const props = block.props;

  const renderBlock = () => {
    switch (block.type) {
      case 'hero':
        return (
          <div 
            className="relative min-h-[300px] bg-cover bg-center flex items-center justify-center text-center p-8"
            style={{ backgroundImage: `url(${props.backgroundImage})` }}
          >
            <div className={`absolute inset-0 ${props.overlay === 'dark' ? 'bg-black/60' : 'bg-white/60'}`} />
            <div className="relative z-10">
              <h1 className={`text-4xl font-bold mb-2 ${props.overlay === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {props.title}
              </h1>
              <p className={`text-lg mb-6 ${props.overlay === 'dark' ? 'text-white/80' : 'text-gray-600'}`}>
                {props.subtitle}
              </p>
              <button className="px-6 py-3 bg-green-500 text-white rounded-full font-bold">
                {props.buttonText}
              </button>
            </div>
          </div>
        );
      
      case 'text':
        return (
          <div className={`p-6 text-${props.align}`}>
            <p className={`text-gray-700 ${props.size === 'large' ? 'text-lg' : props.size === 'small' ? 'text-sm' : ''}`}>
              {props.content}
            </p>
          </div>
        );
      
      case 'heading':
        const HeadingTag = props.level || 'h2';
        const headingSizes = { h1: 'text-4xl', h2: 'text-3xl', h3: 'text-2xl', h4: 'text-xl' };
        return (
          <div className={`p-6 text-${props.align}`}>
            <HeadingTag className={`font-bold text-gray-900 ${headingSizes[props.level]}`}>
              {props.text}
            </HeadingTag>
          </div>
        );
      
      case 'image':
        return (
          <div className="p-4">
            <img loading="lazy" decoding="async" 
              src={props.src} 
              alt={props.alt}
              className={`rounded-lg ${props.size === 'full' ? 'w-full' : props.size === 'medium' ? 'max-w-md mx-auto' : 'max-w-xs mx-auto'}`}
            />
            {props.caption && <p className="text-center text-sm text-gray-500 mt-2">{props.caption}</p>}
          </div>
        );
      
      case 'button':
        const buttonStyles = {
          primary: 'bg-green-500 text-white',
          secondary: 'bg-gray-200 text-gray-800',
          outline: 'border-2 border-green-500 text-green-600',
        };
        return (
          <div className={`p-6 text-${props.align}`}>
            <button className={`px-6 py-3 rounded-full font-bold ${buttonStyles[props.style]}`}>
              {props.text}
            </button>
          </div>
        );
      
      case 'columns':
        return (
          <div className={`p-6 grid grid-cols-${props.columns} gap-${props.gap === 'large' ? '8' : props.gap === 'small' ? '2' : '4'}`}>
            {props.content?.map((col, i) => (
              <div key={i} className="bg-gray-100 p-4 rounded-lg">
                <p className="text-gray-700">{col}</p>
              </div>
            ))}
          </div>
        );
      
      case 'list':
        return (
          <div className="p-6">
            <ul className={props.style === 'numbered' ? 'list-decimal' : 'list-disc'} style={{ paddingLeft: '1.5rem' }}>
              {props.items?.map((item, i) => (
                <li key={i} className="text-gray-700 mb-2">{item}</li>
              ))}
            </ul>
          </div>
        );
      
      case 'quote':
        return (
          <div className={`p-6 ${props.style === 'bordered' ? 'border-l-4 border-green-500 pl-6' : ''}`}>
            <blockquote className="text-xl italic text-gray-700 mb-2">"{props.text}"</blockquote>
            {props.author && <cite className="text-gray-500">— {props.author}</cite>}
          </div>
        );
      
      case 'spacer':
        const spacerHeights = { small: 'h-8', medium: 'h-16', large: 'h-32' };
        return <div className={spacerHeights[props.height]} />;
      
      case 'divider':
        return (
          <div className="p-4">
            <hr className={`border-t ${props.color === 'dark' ? 'border-gray-800' : 'border-gray-300'}`} />
          </div>
        );
      
      case 'video':
        return (
          <div className="p-4">
            <div className={`relative ${props.aspectRatio === '16:9' ? 'pb-[56.25%]' : 'pb-[75%]'}`}>
              <iframe
                src={props.url}
                className="absolute inset-0 w-full h-full rounded-lg"
                allowFullScreen
              />
            </div>
          </div>
        );
      
      case 'cta':
        return (
          <div className={`p-12 text-center ${props.style === 'gradient' ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gray-100'}`}>
            <h2 className={`text-3xl font-bold mb-4 ${props.style === 'gradient' ? 'text-white' : 'text-gray-900'}`}>
              {props.title}
            </h2>
            <p className={`mb-6 ${props.style === 'gradient' ? 'text-white/90' : 'text-gray-600'}`}>
              {props.description}
            </p>
            <button className={`px-8 py-4 rounded-full font-bold ${props.style === 'gradient' ? 'bg-white text-green-600' : 'bg-green-500 text-white'}`}>
              {props.buttonText}
            </button>
          </div>
        );
      
      default:
        return <div className="p-4 text-gray-500">Unknown block type</div>;
    }
  };

  return (
    <div 
      className={`relative bg-white rounded-lg overflow-hidden cursor-pointer transition-all ${
        isSelected ? 'ring-2 ring-green-500 shadow-lg' : 'hover:ring-1 hover:ring-green-300'
      }`}
      onClick={onClick}
    >
      {renderBlock()}
      {isSelected && (
        <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded font-semibold">
          {type.name}
        </div>
      )}
    </div>
  );
};

// ==================== SORTABLE BLOCK ====================
const SortableBlock = ({ block, isSelected, onSelect, onDelete, onDuplicate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group mb-4">
      {/* Drag Handle */}
      <div 
        {...attributes} 
        {...listeners}
        className="absolute -left-10 top-1/2 -translate-y-1/2 p-2 bg-zinc-800 rounded-lg cursor-grab opacity-0 group-hover:opacity-100 transition-opacity z-10"
      >
        <GripVertical className="w-4 h-4 text-zinc-400" />
      </div>
      
      {/* Block Actions */}
      <div className="absolute -right-2 top-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <button 
          onClick={(e) => { e.stopPropagation(); onDuplicate(block.id); }}
          className="p-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-400 hover:text-white"
          title="Duplicate"
        >
          <Copy className="w-3 h-3" />
        </button>
        <button 
          onClick={(e) => { e.stopPropagation(); onDelete(block.id); }}
          className="p-1.5 bg-zinc-800 hover:bg-red-600 rounded text-zinc-400 hover:text-white"
          title="Delete"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>

      <BlockPreview block={block} isSelected={isSelected} onClick={() => onSelect(block.id)} />
    </div>
  );
};

// ==================== BLOCK LIBRARY ====================
const BlockLibrary = ({ onAddBlock }) => {
  const [expandedCategory, setExpandedCategory] = useState('sections');

  return (
    <div className="space-y-4">
      {Object.entries(CATEGORIES).map(([catKey, category]) => {
        const blocksInCategory = Object.entries(BLOCK_TYPES).filter(([_, b]) => b.category === catKey);
        const CategoryIcon = category.icon;
        
        return (
          <div key={catKey} className="border border-zinc-800 rounded-lg overflow-hidden">
            <button
              onClick={() => setExpandedCategory(expandedCategory === catKey ? null : catKey)}
              className="w-full px-4 py-3 flex items-center justify-between bg-zinc-900 hover:bg-zinc-800 transition-colors"
            >
              <div className="flex items-center gap-2">
                <CategoryIcon className="w-4 h-4 text-green-500" />
                <span className="font-medium text-white text-sm">{category.name}</span>
              </div>
              <ChevronRight className={`w-4 h-4 text-zinc-500 transition-transform ${expandedCategory === catKey ? 'rotate-90' : ''}`} />
            </button>
            
            <AnimatePresence>
              {expandedCategory === catKey && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-3 grid grid-cols-2 gap-2">
                    {blocksInCategory.map(([typeKey, blockType]) => {
                      const Icon = blockType.icon;
                      return (
                        <button
                          key={typeKey}
                          onClick={() => onAddBlock(typeKey)}
                          className="p-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex flex-col items-center gap-2 transition-colors"
                        >
                          <Icon className="w-5 h-5 text-green-500" />
                          <span className="text-xs text-zinc-300">{blockType.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

// ==================== PROPERTIES EDITOR ====================
const PropertiesEditor = ({ block, onUpdate }) => {
  if (!block) {
    return (
      <div className="p-6 text-center text-zinc-500">
        <Settings className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <p className="text-sm">Select a block to edit its properties</p>
      </div>
    );
  }

  const blockType = BLOCK_TYPES[block.type];
  const props = block.props;

  const updateProp = (key, value) => {
    onUpdate(block.id, { ...props, [key]: value });
  };

  const renderInput = (key, value, type = 'text') => {
    switch (type) {
      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => updateProp(key, e.target.value)}
            rows={3}
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:border-green-500 focus:outline-none resize-none"
          />
        );
      case 'select':
        return null; // Handled separately
      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => updateProp(key, e.target.value)}
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:border-green-500 focus:outline-none"
          />
        );
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2 pb-3 border-b border-zinc-800">
        <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
          {React.createElement(blockType.icon, { className: 'w-4 h-4 text-green-500' })}
        </div>
        <span className="font-bold text-white">{blockType.name}</span>
      </div>

      {/* Dynamic property fields based on block type */}
      {Object.entries(props).map(([key, value]) => {
        // Skip complex array properties for simple display
        if (Array.isArray(value)) {
          return (
            <div key={key} className="space-y-2">
              <label className="text-xs font-medium text-zinc-400 capitalize">{key.replace(/_/g, ' ')}</label>
              {value.map((item, i) => (
                <input
                  key={i}
                  type="text"
                  value={item}
                  onChange={(e) => {
                    const newArray = [...value];
                    newArray[i] = e.target.value;
                    updateProp(key, newArray);
                  }}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:border-green-500 focus:outline-none"
                />
              ))}
              <button
                onClick={() => updateProp(key, [...value, `Item ${value.length + 1}`])}
                className="text-xs text-green-500 hover:text-green-400"
              >
                + Add item
              </button>
            </div>
          );
        }

        // Select fields for specific properties
        if (['align', 'style', 'size', 'level', 'overlay', 'height', 'gap', 'columns'].includes(key)) {
          const options = {
            align: ['left', 'center', 'right'],
            style: block.type === 'button' ? ['primary', 'secondary', 'outline'] : block.type === 'list' ? ['bullet', 'numbered'] : block.type === 'quote' ? ['bordered', 'simple'] : block.type === 'cta' ? ['gradient', 'simple'] : ['line', 'dashed'],
            size: ['small', 'medium', 'large', 'full'],
            level: ['h1', 'h2', 'h3', 'h4'],
            overlay: ['dark', 'light'],
            height: ['small', 'medium', 'large'],
            gap: ['small', 'medium', 'large'],
            columns: ['2', '3', '4'],
          };

          return (
            <div key={key} className="space-y-1">
              <label className="text-xs font-medium text-zinc-400 capitalize">{key.replace(/_/g, ' ')}</label>
              <select
                value={value}
                onChange={(e) => updateProp(key, key === 'columns' ? parseInt(e.target.value) : e.target.value)}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:border-green-500 focus:outline-none"
              >
                {options[key]?.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          );
        }

        // Text inputs for other properties
        const isLongText = ['content', 'description', 'text', 'subtitle'].includes(key);
        return (
          <div key={key} className="space-y-1">
            <label className="text-xs font-medium text-zinc-400 capitalize">{key.replace(/_/g, ' ')}</label>
            {renderInput(key, value, isLongText ? 'textarea' : 'text')}
          </div>
        );
      })}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
export default function LivePageEditor() {
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [pageName, setPageName] = useState('New Page');
  const [pageSlug, setPageSlug] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState([]);
  const [activePageId, setActivePageId] = useState(null);
  const [activeTab, setActiveTab] = useState('blocks'); // 'blocks' or 'properties'
  const [showPublishModal, setShowPublishModal] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    loadPages();
  }, []);

  const loadPages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/live-pages`);
      if (response.ok) {
        const data = await response.json();
        setPages(data);
        if (data.length > 0) {
          loadPage(data[0]._id || data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPage = async (pageId) => {
    try {
      const response = await fetch(`${API_URL}/api/live-pages/${pageId}`);
      if (response.ok) {
        const data = await response.json();
        setBlocks(data.blocks || []);
        setPageName(data.name || 'Untitled Page');
        setPageSlug(data.slug || '');
        setIsPublished(data.published || false);
        setActivePageId(pageId);
      }
    } catch (error) {
      console.error('Failed to load page:', error);
    }
  };

  const savePage = async () => {
    setSaving(true);
    try {
      const method = activePageId ? 'PUT' : 'POST';
      const url = activePageId ? `${API_URL}/api/live-pages/${activePageId}` : `${API_URL}/api/live-pages`;
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: pageName, blocks }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (!activePageId) {
          setActivePageId(data.id || data._id);
          loadPages();
        }
        toast.success('Page saved successfully!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const addBlock = (type) => {
    const blockType = BLOCK_TYPES[type];
    const newBlock = {
      id: `block_${Date.now()}`,
      type,
      props: { ...blockType.defaultProps },
    };
    setBlocks([...blocks, newBlock]);
    setSelectedBlockId(newBlock.id);
    setActiveTab('properties');
  };

  const deleteBlock = (blockId) => {
    setBlocks(blocks.filter(b => b.id !== blockId));
    if (selectedBlockId === blockId) {
      setSelectedBlockId(null);
    }
  };

  const duplicateBlock = (blockId) => {
    const block = blocks.find(b => b.id === blockId);
    if (block) {
      const newBlock = {
        ...block,
        id: `block_${Date.now()}`,
        props: { ...block.props },
      };
      const index = blocks.findIndex(b => b.id === blockId);
      const newBlocks = [...blocks];
      newBlocks.splice(index + 1, 0, newBlock);
      setBlocks(newBlocks);
    }
  };

  const updateBlockProps = (blockId, newProps) => {
    setBlocks(blocks.map(b => b.id === blockId ? { ...b, props: newProps } : b));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = blocks.findIndex(b => b.id === active.id);
      const newIndex = blocks.findIndex(b => b.id === over.id);
      setBlocks(arrayMove(blocks, oldIndex, newIndex));
    }
  };

  const selectedBlock = blocks.find(b => b.id === selectedBlockId);

  const newPage = () => {
    setBlocks([]);
    setPageName('New Page');
    setPageSlug('');
    setIsPublished(false);
    setActivePageId(null);
    setSelectedBlockId(null);
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const publishPage = async () => {
    if (!activePageId) {
      toast.error('Please save the page first');
      return;
    }
    
    const slug = pageSlug || generateSlug(pageName);
    if (!slug) {
      toast.error('Please enter a valid URL slug');
      return;
    }

    setPublishing(true);
    try {
      const response = await fetch(`${API_URL}/api/live-pages/${activePageId}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setPageSlug(data.slug);
        setIsPublished(true);
        setShowPublishModal(false);
        loadPages();
        toast.success(`Page published at /p/${data.slug}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to publish');
      }
    } catch (error) {
      toast.error('Failed to publish page');
    } finally {
      setPublishing(false);
    }
  };

  const unpublishPage = async () => {
    if (!activePageId) return;
    
    setPublishing(true);
    try {
      const response = await fetch(`${API_URL}/api/live-pages/${activePageId}/unpublish`, {
        method: 'POST',
      });
      
      if (response.ok) {
        setIsPublished(false);
        loadPages();
        toast.success('Page unpublished');
      } else {
        toast.error('Failed to unpublish');
      }
    } catch (error) {
      toast.error('Failed to unpublish page');
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 bg-black border-b border-zinc-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/admin/dashboard')} className="p-2 hover:bg-zinc-800 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <Layout className="w-5 h-5 text-green-500" />
            <input
              type="text"
              value={pageName}
              onChange={(e) => setPageName(e.target.value)}
              className="bg-transparent text-lg font-bold focus:outline-none border-b border-transparent focus:border-green-500"
            />
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Published status badge */}
          {isPublished && pageSlug && (
            <a
              href={`/p/${pageSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 bg-green-500/20 text-green-400 rounded-lg flex items-center gap-2 text-sm hover:bg-green-500/30"
            >
              <Globe className="w-4 h-4" />
              Live
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          
          <button
            onClick={() => setShowPreview(!showPreview)}
            className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${
              showPreview ? 'bg-green-500/20 text-green-400' : 'bg-zinc-800 hover:bg-zinc-700'
            }`}
          >
            {showPreview ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {showPreview ? 'Preview On' : 'Preview'}
          </button>
          
          <button onClick={newPage} className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm">
            <Plus className="w-4 h-4" />
          </button>
          
          <button
            onClick={savePage}
            disabled={saving}
            className="px-5 py-2 bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save'}
          </button>

          {/* Publish Button */}
          {activePageId && (
            isPublished ? (
              <button
                onClick={unpublishPage}
                disabled={publishing}
                className="px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 font-bold rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
              >
                {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                Unpublish
              </button>
            ) : (
              <button
                onClick={() => setShowPublishModal(true)}
                className="px-5 py-2 bg-green-500 hover:bg-green-400 text-black font-bold rounded-lg flex items-center gap-2 text-sm"
              >
                <Globe className="w-4 h-4" />
                Publish
              </button>
            )
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Block Library / Properties */}
        <div className="w-72 border-r border-zinc-800 flex flex-col bg-zinc-900">
          {/* Tabs */}
          <div className="flex border-b border-zinc-800">
            <button
              onClick={() => setActiveTab('blocks')}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'blocks' ? 'text-green-500 border-b-2 border-green-500' : 'text-zinc-400 hover:text-white'
              }`}
            >
              <Plus className="w-4 h-4 inline mr-2" />
              Blocks
            </button>
            <button
              onClick={() => setActiveTab('properties')}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'properties' ? 'text-green-500 border-b-2 border-green-500' : 'text-zinc-400 hover:text-white'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Properties
            </button>
          </div>
          
          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {activeTab === 'blocks' ? (
              <BlockLibrary onAddBlock={addBlock} />
            ) : (
              <PropertiesEditor block={selectedBlock} onUpdate={updateBlockProps} />
            )}
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-y-auto bg-zinc-950 p-8">
          {showPreview ? (
            /* Full Preview Mode */
            <div className="max-w-4xl mx-auto bg-white rounded-xl overflow-hidden shadow-2xl">
              {blocks.length === 0 ? (
                <div className="py-32 text-center text-gray-400">
                  <Layout className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>No blocks added yet</p>
                </div>
              ) : (
                blocks.map(block => (
                  <BlockPreview key={block.id} block={block} isSelected={false} onClick={() => {}} />
                ))
              )}
            </div>
          ) : (
            /* Edit Mode with Drag & Drop */
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <div className="max-w-4xl mx-auto">
                {blocks.length === 0 ? (
                  <div className="border-2 border-dashed border-zinc-700 rounded-xl py-32 text-center">
                    <Plus className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
                    <p className="text-zinc-500 mb-2">Start building your page</p>
                    <p className="text-zinc-600 text-sm">Click a block from the left panel to add it</p>
                  </div>
                ) : (
                  <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                    <div className="pl-12">
                      {blocks.map(block => (
                        <SortableBlock
                          key={block.id}
                          block={block}
                          isSelected={selectedBlockId === block.id}
                          onSelect={setSelectedBlockId}
                          onDelete={deleteBlock}
                          onDuplicate={duplicateBlock}
                        />
                      ))}
                    </div>
                  </SortableContext>
                )}
                
                {/* Add Block Button at Bottom */}
                {blocks.length > 0 && (
                  <div className="mt-8 text-center">
                    <button
                      onClick={() => setActiveTab('blocks')}
                      className="px-6 py-3 border-2 border-dashed border-zinc-700 hover:border-green-500 text-zinc-500 hover:text-green-500 rounded-xl transition-colors"
                    >
                      <Plus className="w-5 h-5 inline mr-2" />
                      Add Block
                    </button>
                  </div>
                )}
              </div>
            </DndContext>
          )}
        </div>

        {/* Right Sidebar - Page List (optional, shown when not in preview) */}
        {!showPreview && pages.length > 0 && (
          <div className="w-56 border-l border-zinc-800 bg-zinc-900 p-4">
            <h3 className="text-xs font-bold text-zinc-500 uppercase mb-3">Saved Pages</h3>
            <div className="space-y-2">
              {pages.map(page => (
                <button
                  key={page._id || page.id}
                  onClick={() => loadPage(page._id || page.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    (page._id || page.id) === activePageId 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'hover:bg-zinc-800 text-zinc-400'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{page.name || 'Untitled'}</span>
                    {page.published && <Globe className="w-3 h-3 text-green-500" />}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Publish Modal */}
      <AnimatePresence>
        {showPublishModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPublishModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-zinc-900 rounded-2xl p-6 max-w-md w-full border border-zinc-800"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-green-500" />
                  Publish Page
                </h2>
                <button
                  onClick={() => setShowPublishModal(false)}
                  className="p-2 hover:bg-zinc-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-zinc-400 block mb-2">
                    Page URL
                  </label>
                  <div className="flex items-center bg-zinc-800 rounded-lg overflow-hidden">
                    <span className="px-3 py-3 text-zinc-500 text-sm bg-zinc-900">/p/</span>
                    <input
                      type="text"
                      value={pageSlug || generateSlug(pageName)}
                      onChange={(e) => setPageSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      placeholder="my-page-url"
                      className="flex-1 px-3 py-3 bg-transparent text-white focus:outline-none text-sm"
                    />
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">
                    Your page will be accessible at: {window.location.origin}/p/{pageSlug || generateSlug(pageName)}
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowPublishModal(false)}
                    className="flex-1 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={publishPage}
                    disabled={publishing}
                    className="flex-1 px-4 py-3 bg-green-500 hover:bg-green-400 text-black font-bold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {publishing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    {publishing ? 'Publishing...' : 'Publish'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
